import { useState } from "react";
import { clsx } from "clsx";
import { CalendarClock } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { Spinner } from "@/components/ui/Spinner";
import { EmptyState, SectionCard, SubTabs, TableShell, Th } from "@/features/power_projects/components/ui";
import * as api from "../api";
import { useLedMutation, useReports } from "../hooks";
import type { ReportCycleRow, ReportFrequency, ReportItemRow, ReportItemStatus, ReportRecipient } from "../types";
import { ErrorLine, Field, FileChips, FilePick, INPUT_SM, OnlyUs, TEXT_OK, fmtDate, fmtDateTime, today } from "./common";

const STATUS_BADGE: Record<ReportItemStatus, "green" | "yellow" | "red" | "gray" | "blue"> = {
  forwarded: "green",
  submitted: "blue",
  overdue: "red",
  due_today: "yellow",
  planned: "gray",
};
const STATUS_LABEL: Record<ReportItemStatus, string> = {
  forwarded: "przekazany Zamawiającemu",
  submitted: "złożony",
  overdue: "po terminie",
  due_today: "dziś termin",
  planned: "zaplanowany",
};
export const WEEKDAYS = ["poniedziałek", "wtorek", "środa", "czwartek", "piątek", "sobota", "niedziela"];
type Filter = "open" | "done" | "all";

/** Jedno zdanie opisujące cykl — „co tydzień, piątek" zamiast trzech pól obok siebie. */
export function cycleRhythm(c: { frequency: ReportFrequency; weekday: number; day_of_month: number }): string {
  return c.frequency === "weekly" ? `co tydzień · ${WEEKDAYS[c.weekday]}` : `co miesiąc · ${c.day_of_month}. dnia`;
}

function CycleCard({ leadershipId, cycle, internal }: { leadershipId: string; cycle: ReportCycleRow; internal: boolean }) {
  const patch = useLedMutation(
    leadershipId,
    (is_active: boolean) => api.patchReportCycle(leadershipId, cycle.id, { is_active }),
    ["reports"],
  );
  return (
    <div
      className={clsx("card flex flex-col gap-1 p-3", !cycle.is_active && "opacity-60")}
      style={{ borderLeft: `3px solid ${cycle.is_active ? "var(--color-primary)" : "var(--color-border-strong)"}` }}
    >
      <div className="flex items-baseline gap-2">
        <CalendarClock size={14} className="text-[var(--color-text-muted)]" />
        <strong className="text-[13px]">{cycle.label || `Raport ${cycle.frequency_display.toLowerCase()}`}</strong>
        {!cycle.is_active && <Badge color="gray">wyłączony</Badge>}
        {internal && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            disabled={patch.isPending}
            onClick={() => patch.mutate(!cycle.is_active)}
          >
            {cycle.is_active ? "wyłącz" : "włącz"}
          </Button>
        )}
      </div>
      <div className="text-[12px] text-[var(--color-text-secondary)]">{cycleRhythm(cycle)}</div>
      <div className="text-[11px] text-[var(--color-text-muted)]">
        odbiorca: {cycle.recipient_display} · od {fmtDate(cycle.starts_on)}
      </div>
      <ErrorLine error={patch.error} />
    </div>
  );
}

function SubmitCell({ leadershipId, item }: { leadershipId: string; item: ReportItemRow }) {
  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState("");
  const submit = useLedMutation(leadershipId, () => api.submitReport(leadershipId, item.id, { files, note }), ["reports"]);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilePick
        variant="secondary"
        onFiles={(f) => setFiles((cur) => [...cur, ...f])}
        label={files.length ? `plik: ${files.map((f) => f.name).join(", ")}` : "Wybierz plik raportu"}
        busy={submit.isPending}
      />
      {files.length > 0 && (
        <>
          <input
            className={clsx(INPUT_SM, "w-40")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="uwaga (opcjonalnie)"
          />
          <Button
            size="sm"
            disabled={submit.isPending}
            onClick={async () => {
              await submit.mutateAsync();
              setFiles([]);
              setNote("");
            }}
          >
            Złóż raport
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setFiles([])}>Wyczyść</Button>
        </>
      )}
      <ErrorLine error={submit.error} />
    </div>
  );
}

/** Harmonogram raportów — cykle ustawiamy my (także od razu w dialogu przekazania);
 *  firma składa plik, my przekazujemy Zamawiającemu. Zero AI, zero porad. */
export function ReportsTab({ leadershipId, internal }: { leadershipId: string; internal: boolean }) {
  const { data, isLoading, error } = useReports(leadershipId);
  const [recipient, setRecipient] = useState<ReportRecipient>("client");
  const [frequency, setFrequency] = useState<ReportFrequency>("weekly");
  const [starts, setStarts] = useState(today());
  const [weekday, setWeekday] = useState(4);
  const [dom, setDom] = useState(5);
  const [label, setLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState<Filter>("open");
  const create = useLedMutation(
    leadershipId,
    () => api.createReportCycle(leadershipId, { recipient, frequency, starts_on: starts, weekday, day_of_month: dom, label }),
    ["reports"],
  );
  const forward = useLedMutation(leadershipId, (id: string) => api.forwardReport(leadershipId, id), ["reports"]);
  if (isLoading || !data)
    return <div className="flex h-40 items-center justify-center">{error ? <ErrorLine error={error} /> : <Spinner />}</div>;

  const all = [...data.items].sort((a, b) => (a.due_date < b.due_date ? 1 : -1));
  const open = all.filter((i) => !i.submitted_at);
  const done = all.filter((i) => i.submitted_at);
  const items = filter === "open" ? open : filter === "done" ? done : all;

  return (
    <div className="space-y-3">
      <SectionCard
        title="Cykle raportów"
        action={
          internal ? (
            <Button size="sm" variant={adding ? "ghost" : "secondary"} onClick={() => setAdding((v) => !v)}>
              {adding ? "Anuluj" : "+ cykl"}
            </Button>
          ) : undefined
        }
        bodyClassName="p-3"
      >
        {data.cycles.length === 0 ? (
          <p className="text-[13px] text-[var(--color-text-muted)]">
            {internal
              ? "Brak cykli — dodaj tygodniowy albo miesięczny. Cykl można też ustawić od razu przy przekazaniu projektu."
              : "GK ELEKTIM nie ustawił jeszcze harmonogramu."}
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {data.cycles.map((c) => (
              <CycleCard key={c.id} leadershipId={leadershipId} cycle={c} internal={internal} />
            ))}
          </div>
        )}
        {internal && adding && (
          <OnlyUs className="mt-3">
            <div className="mt-1 flex flex-wrap items-end gap-3">
              <Field label="Odbiorca" className="w-[13rem]">
                <select className={INPUT_SM} value={recipient} onChange={(e) => setRecipient(e.target.value as ReportRecipient)}>
                  <option value="client">Zamawiający (przez nas)</option>
                  <option value="gk">GK ELEKTIM</option>
                </select>
              </Field>
              <Field label="Częstotliwość" className="w-[9rem]">
                <select className={INPUT_SM} value={frequency} onChange={(e) => setFrequency(e.target.value as ReportFrequency)}>
                  <option value="weekly">tygodniowo</option>
                  <option value="monthly">miesięcznie</option>
                </select>
              </Field>
              {frequency === "weekly" ? (
                <Field label="Dzień tygodnia" className="w-[9rem]">
                  <select className={INPUT_SM} value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
                    {WEEKDAYS.map((w, i) => (
                      <option key={w} value={i}>{w}</option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Field label="Dzień miesiąca" className="w-[7rem]">
                  <input type="number" min={1} max={28} className={INPUT_SM} value={dom} onChange={(e) => setDom(Number(e.target.value))} />
                </Field>
              )}
              <Field label="Od kiedy" className="w-[10rem]">
                <DatePicker value={starts} clearable={false} className="!py-1 text-[13px]" onChange={(v) => setStarts(v || today())} />
              </Field>
              <Field label="Nazwa (opcjonalnie)" className="min-w-[14rem] flex-1">
                <input className={INPUT_SM} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="np. Raport tygodniowy ENERGA" />
              </Field>
              <Button
                size="sm"
                className="mb-px"
                disabled={create.isPending}
                onClick={async () => {
                  await create.mutateAsync();
                  setAdding(false);
                }}
              >
                Zapisz cykl
              </Button>
            </div>
            <p className="mt-1.5 text-[11px] text-[var(--color-text-muted)]">
              Pierwszy termin pojawi się od razu na liście poniżej; firma składa plik, my przekazujemy Zamawiającemu.
            </p>
            <ErrorLine error={create.error} />
          </OnlyUs>
        )}
      </SectionCard>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <SubTabs<Filter>
          active={filter}
          onChange={setFilter}
          tabs={[
            { key: "open", label: "Do złożenia", count: open.length || undefined, countTone: open.some((i) => i.status === "overdue") ? "danger" : "warn" },
            { key: "done", label: "Złożone", count: done.length || undefined, countTone: "blue" },
            { key: "all", label: "Wszystkie", count: all.length || undefined },
          ]}
        />
      </div>

      {items.length === 0 ? (
        <div className="card"><EmptyState>{all.length === 0 ? "Brak raportów w harmonogramie" : "Nic w tym widoku"}</EmptyState></div>
      ) : (
        <TableShell tableClassName="text-[13px]">
          <thead>
            <tr>
              <Th>Termin</Th>
              <Th>Okres</Th>
              <Th>Odbiorca</Th>
              <Th>Stan</Th>
              <Th>Plik i działania</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-[var(--color-border-muted)] align-top">
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs font-bold">{fmtDate(it.due_date)}</td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">
                  {fmtDate(it.period_start)} – {fmtDate(it.period_end)}
                  <span className="mt-0.5 block font-sans text-[11px] text-[var(--color-text-muted)]">
                    {it.label || it.frequency_display}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-2">{it.recipient_display}</td>
                <td className="whitespace-nowrap px-3 py-2">
                  <Badge color={STATUS_BADGE[it.status]}>{STATUS_LABEL[it.status]}</Badge>
                </td>
                <td className="px-3 py-2">
                  {it.submitted_at ? (
                    <>
                      <div className="text-[11px] text-[var(--color-text-muted)]">
                        złożył {it.submitted_by_label} · {fmtDateTime(it.submitted_at)}
                        {it.note && <> · {it.note}</>}
                      </div>
                      <FileChips files={it.files} className="mt-1" />
                      {it.forwarded_at ? (
                        <div className={clsx("mt-1 text-[11px]", TEXT_OK)}>
                          przekazał Zamawiającemu {it.forwarded_by_label} · {fmtDateTime(it.forwarded_at)}
                        </div>
                      ) : internal && it.recipient === "client" ? (
                        <Button size="sm" className="mt-1.5" disabled={forward.isPending} onClick={() => forward.mutate(it.id)}>
                          Przekazano Zamawiającemu
                        </Button>
                      ) : null}
                    </>
                  ) : (
                    <SubmitCell leadershipId={leadershipId} item={it} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}
      <ErrorLine error={forward.error} />
    </div>
  );
}
